import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ChartLineUp, Lightning, ShieldCheck, Lock,
  EnvelopeSimple, CheckCircle, Trophy, Calendar, Bell, ArrowLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CONFIGS = {
  stocks: {
    label: "Stock Picks",
    brand: "Accurate Monkey Stocks",
    hero: ["SWING TRADES,", "SETTLED EARLY."],
    tagline: "Mid-cap momentum names with 3–10 day holds. 2–3 picks per week. Entry, stop, and price target attached to every alert.",
    price: "$25",
    cadence: "week",
    accent: "#F59E0B",
    accentSoft: "#F59E0B22",
    icon: ChartLineUp,
    productKey: "stocks",
    launchTarget: "Q3 2026",
    quickStats: [
      { label: "Cadence", value: "2-3 picks/week" },
      { label: "Hold Window", value: "3-10 days" },
      { label: "Exit Alert", value: "Yes" },
      { label: "Launches", value: "Q3 2026" },
    ],
    methodology: [
      { title: "Universe", body: "Mid-cap U.S. equities · liquid float · earnings + flow-driven setups." },
      { title: "Entry signal", body: "Multi-day base breaks confirmed by volume + relative-strength rotation." },
      { title: "Risk model", body: "Hard stop at 5-7% below entry · position size scales with conviction." },
      { title: "Exits", body: "Trim at first price target · trailing stop on the remainder · alerts when we cut." },
    ],
    valueProps: [
      { icon: Calendar, title: "Weekly Rhythm", body: "2–3 picks/week — designed for traders who don't stare at the tape all day." },
      { icon: Bell, title: "Entry + Exit Alerts", body: "We tell you when we get in AND when we get out. No abandoned positions." },
      { icon: Trophy, title: "Public Track Record", body: "Every pick lands in our tamper-evident ledger. You'll see the wins and the losses." },
    ],
  },
  options: {
    label: "Option Picks",
    brand: "Accurate Monkey Options",
    hero: ["HIGH CONVICTION.", "FAST TURNAROUND."],
    tagline: "Single-leg + spreads on highly-liquid tickers. 0DTE same-day plays plus weekly setups. Entry strike, stop, and exit trigger on every alert.",
    price: "$35",
    cadence: "week",
    accent: "#A855F7",
    accentSoft: "#A855F722",
    icon: Lightning,
    productKey: "options",
    launchTarget: "Q4 2026",
    quickStats: [
      { label: "Cadence", value: "Daily 0DTE + weekly" },
      { label: "Style", value: "Single-leg + spreads" },
      { label: "Exit Alert", value: "Yes" },
      { label: "Launches", value: "Q4 2026" },
    ],
    methodology: [
      { title: "Universe", body: "Top-50 most liquid options (SPY, QQQ, large-cap names) · tight bid/ask spreads only." },
      { title: "Entry signal", body: "Gamma flow + dealer positioning + technical confluence — no naked guessing." },
      { title: "Risk model", body: "Hard stop on premium loss · sized so a full loss is ≤2% of bankroll." },
      { title: "Exits", body: "Pre-defined profit targets · roll/cut alerts before theta + gamma slip away." },
    ],
    valueProps: [
      { icon: Lightning, title: "0DTE + Weeklies", body: "Same-day plays for the active trader, weekly setups for swing-style players." },
      { icon: Bell, title: "Entry + Exit Alerts", body: "Every pick tells you exactly when to open, where to cut, and where to take profit." },
      { icon: Trophy, title: "Public Track Record", body: "We don't hide losers. Every trade — winner or not — is in the public ledger." },
    ],
  },
  "all-access": {
    label: "All Access",
    brand: "Accurate Monkey · All Access",
    hero: ["ONE LOGIN.", "EVERY PICK."],
    tagline: "MLB picks + stock swing trades + option plays — bundled. Save $25/week vs. buying separately.",
    price: "$50",
    cadence: "week",
    accent: "#22C55E",
    accentSoft: "#22C55E22",
    icon: Trophy,
    productKey: "all_access",
    launchTarget: "Q3 2026",
    quickStats: [
      { label: "Includes", value: "MLB · Stocks · Options" },
      { label: "Save", value: "$25 /week" },
      { label: "Cancel", value: "Anytime" },
      { label: "Trial", value: "7 days free" },
    ],
    methodology: [
      { title: "MLB", body: "Daily picks across 30 teams — bounce-back & momentum patterns. Locked before first pitch." },
      { title: "Stocks", body: "Swing trades, 2-3/week, mid-cap momentum names. Entry + exit alerts." },
      { title: "Options", body: "0DTE + weekly plays on the most liquid tickers. Strike, stop, exit on every alert." },
      { title: "Bundle math", body: "Buy separately = $75/wk. All Access = $50/wk. Save $25/wk forever." },
    ],
    valueProps: [
      { icon: ShieldCheck, title: "One Subscription, Three Products", body: "One login, one bill, one account. Manage everything from /account." },
      { icon: CheckCircle, title: "Cancel Each Independently", body: "Drop the parts you don't want without losing the others — controlled from your account." },
      { icon: Trophy, title: "All Track Records Public", body: "Each product publishes its own tamper-evident receipt ledger. No hiding." },
    ],
  },
};

export default function ProductTeaser() {
  const { product } = useParams();
  const cfg = CONFIGS[product];
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  if (!cfg) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-20 text-center" data-testid="product-not-found">
        <h1 className="font-display text-5xl mb-4">Product not found</h1>
        <Link to="/" className="text-[#007AFF] hover:underline" data-testid="back-home-link">← Back to home</Link>
      </div>
    );
  }

  const Icon = cfg.icon;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || email.length < 4) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist", { email: email.trim(), name: name.trim() || null, product: cfg.productKey });
      setJoined(true);
      toast.success(data.already_subscribed ? "You're already on the list" : "You're on the waitlist. We'll email you at launch.");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Couldn't join waitlist");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[calc(100vh-180px)]" data-testid={`teaser-${cfg.productKey}`}>
      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 pt-12 pb-8">
        <Link to="/" data-testid="back-products-link" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-8">
          <ArrowLeft size={12} weight="bold" /> All products
        </Link>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: cfg.accent }} data-testid="teaser-badge">
            <Icon size={14} weight="fill" /> {cfg.label} · Launching {cfg.launchTarget}
          </div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] mb-5">
            {cfg.hero[0]}<br />
            <span style={{ color: cfg.accent }}>{cfg.hero[1]}</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">{cfg.tagline}</p>
        </motion.div>
      </section>

      {/* Quick stats */}
      <section className="max-w-[1280px] mx-auto px-6 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cfg.quickStats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
              className="bg-[#121212] border border-white/10 rounded-sm p-4"
              data-testid={`teaser-stat-${i}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</div>
              <div className="font-display text-2xl mt-1" style={{ color: cfg.accent }}>{s.value}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Waitlist signup */}
      <section className="max-w-[1280px] mx-auto px-6 pb-12">
        <div className="bg-[#0f0f0f] border-2 rounded-sm p-6 sm:p-8" style={{ borderColor: `${cfg.accent}66` }} data-testid="waitlist-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: cfg.accent }}>
                <Bell size={12} className="inline mb-1 mr-1" weight="fill" /> Be first to know
              </div>
              <h3 className="font-display text-3xl sm:text-4xl leading-tight mb-3">
                Get launch-week pricing + a free week of {cfg.brand}.
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Drop your email. We'll send one note the moment {cfg.brand} goes live, with a launch-week discount code. No spam — just the heads-up.
              </p>
            </div>

            {joined ? (
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="rounded-sm p-5 text-center"
                style={{ background: cfg.accentSoft, border: `1px solid ${cfg.accent}66` }}
                data-testid="waitlist-success"
              >
                <CheckCircle size={32} weight="fill" style={{ color: cfg.accent }} className="mx-auto mb-2" />
                <div className="font-display text-2xl mb-1">You're in.</div>
                <div className="text-sm text-zinc-400">We'll email <span className="text-white font-mono-d">{email}</span> at launch.</div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">Name (optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    maxLength={120}
                    data-testid="waitlist-name"
                    className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none rounded-sm px-3 py-2.5 text-sm font-mono-d transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5">Email</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <EnvelopeSimple size={14} weight="fill" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        data-testid="waitlist-email"
                        className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none rounded-sm pl-9 pr-3 py-2.5 text-sm font-mono-d transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="waitlist-submit"
                      style={{ background: cfg.accent }}
                      className="px-5 py-2.5 text-black text-xs uppercase tracking-[0.2em] font-bold rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                      {submitting ? "Joining…" : "Join"} <ArrowRight size={12} weight="bold" />
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="max-w-[1280px] mx-auto px-6 pb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">How it works</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cfg.methodology.map((m, i) => (
            <div key={m.title} className="bg-[#121212] border border-white/10 rounded-sm p-5" data-testid={`teaser-method-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: cfg.accent }}>{m.title}</div>
              <div className="text-sm text-zinc-300 leading-relaxed">{m.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-[1280px] mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cfg.valueProps.map((v, i) => {
            const VIcon = v.icon;
            return (
              <div key={v.title} className="bg-[#0f0f0f] border border-white/10 rounded-sm p-5" data-testid={`teaser-value-${i}`}>
                <VIcon size={22} weight="fill" style={{ color: cfg.accent }} className="mb-3" />
                <div className="font-display text-xl mb-2">{v.title}</div>
                <div className="text-sm text-zinc-400 leading-relaxed">{v.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA strip */}
      <section className="max-w-[1280px] mx-auto px-6 pb-20">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sm:p-8 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: cfg.accent }}>Pricing at launch</div>
            <div className="font-display text-4xl sm:text-5xl">
              <span style={{ color: cfg.accent }}>{cfg.price}</span>
              <span className="text-zinc-500 text-xl"> /{cfg.cadence}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-1">First 7 days free · cancel anytime</div>
          </div>
          <div className="flex gap-2 items-center">
            {!joined && (
              <button
                onClick={() => document.querySelector("[data-testid='waitlist-email']")?.focus()}
                data-testid="cta-jump-waitlist"
                className="px-5 py-2.5 bg-white text-black text-[11px] uppercase tracking-[0.2em] font-bold rounded-sm hover:bg-zinc-200 inline-flex items-center gap-1.5"
              >
                Join Waitlist <ArrowRight size={12} weight="bold" />
              </button>
            )}
            <Link
              to="/sports/mlb"
              data-testid="cta-try-mlb"
              className="px-5 py-2.5 border border-white/20 hover:border-white text-[11px] uppercase tracking-[0.2em] font-bold rounded-sm text-zinc-300 hover:text-white"
            >
              Try MLB Picks now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
